import { useState, useEffect } from 'react';
import {
  InstagramIcon,
  FacebookIconOutline,
  TwitterIcon,
  LinkedInIcon
} from '../public/icons';
import { PrimaryLogo } from '../public/images/';
import newsletterService from '../services/newsletterService';
import {  LogoIcon } from '../public/icons';
const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });

  /* Auto-dismiss the status message after 5 seconds */
  useEffect(() => {
    if (!status.message) return;
    const id = setTimeout(() => {
      setStatus({ type: '', message: '' });
    }, 5000);
    return () => clearTimeout(id);
  }, [status.message]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setStatus({ type: '', message: '' });
    setIsSubmitting(true);

    try {
      const res = await newsletterService.subscribe(email, 'footer');

      setStatus({
        type: 'success',
        message: res?.alreadySubscribed
          ? "You're already on the list."
          : 'Thanks for subscribing! Check your inbox soon.'
      });
      setEmail('');
    } catch (err) {
      setStatus({
        type: 'error',
        message:
          err?.response?.data?.message ||
          err?.message ||
          'Could not subscribe. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-secondary text-white">
      {/* Newsletter Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="text-center mb-16">
          <h2 className="text-3xl text-[#FCF7EE] sm:text-4xl lg:text-4xl font-bold mb-6 leading-tight">
            Stay Connected
            <br />
            to the Stories That Matter
          </h2>
          <p className="text-lg text-[#FCF7EE] sm:text-xl font-normal mb-8 max-w-2xl mx-auto leading-relaxed">
            Receive thoughtful updates, new memory gifts, and
            <br />
            stories that remind you what's worth holding on to.
          </p>

          {/* Newsletter Form */}
          <form
            onSubmit={handleSubmit}
            className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
          >
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (status.message) setStatus({ type: '', message: '' });
              }}
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 text-gray-900 placeholder-secondary focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
              required
              disabled={isSubmitting}
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-8 py-3 bg-gray-200 hover:bg-gray-100 text-[#1C2A46] font-semibold transition-colors duration-200 disabled:opacity-60 disabled:cursor-wait"
            >
              {isSubmitting ? 'Subscribing…' : 'Subscribe'}
            </button>
          </form>

          {/* Inline status message */}
          {status.message && (
            <p
              className={`mt-4 text-sm ${
                status.type === 'success' ? 'text-[#A5E1B9]' : 'text-[#FCA5A5]'
              }`}
            >
              {status.message}
            </p>
          )}
        </div>

        {/* Divider */}
        <div className="border-t border-[#AEAEAE] mb-16"></div>

        {/* Footer Content */}
        <div className="flex max-sm:flex-col justify-between text-left">
          {/* Logo and Description */}
          <div className="">
            <LogoIcon
                className="hover:opacity-80 transition-opacity duration-200"
                color="#ffffff"
                size={40}
              />

            <p className="text-gray-300 text-sm leading-relaxed w-full">
              Kapta helps you capture, gift, and
              <br />
              preserve memories — beautifully.
            </p>
          </div>

          <div className=" flex gap-28 max-sm:flex-col max-sm:mt-10">{/* Explore Links */}
          <div className="lg:col-span-1">
            <h3 className="text-lg font-semibold mb-4">Explore</h3>
            <ul className="space-y-3">
              <li>
                <a
                  href="/shop"
                  className="text-gray-300 hover:text-white transition-colors text-sm"
                >
                  Shop
                </a>
              </li>
              <li>
                <a
                  href="/memoirs"
                  className="text-gray-300 hover:text-white transition-colors text-sm"
                >
                  Memoirs
                </a>
              </li>
            </ul>
          </div>

          {/* Company Links */}
          <div className="lg:col-span-1">
            <h3 className="text-lg font-semibold mb-4">Company</h3>
            <ul className="space-y-3">
              <li>
                <a
                  href="/blog"
                  className="text-gray-300 hover:text-white transition-colors text-sm"
                >
                  Blog
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="text-gray-300 hover:text-white transition-colors text-sm"
                >
                  Contact
                </a>
              </li>
            </ul>
          </div>

          {/* Legal Links */}
          <div className="lg:col-span-1">
            <h3 className="text-lg font-semibold mb-4">Legal</h3>
            <ul className="space-y-3">
              <li>
                <a
                  href="#"
                  className="text-gray-300 hover:text-white transition-colors text-sm"
                >
                  Terms of Service
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="text-gray-300 hover:text-white transition-colors text-sm"
                >
                  Privacy Policy
                </a>
              </li>
            </ul>
          </div></div>
        </div>

        {/* Bottom Section */}
        <div className="flex flex-col sm:flex-row justify-between items-center mt-5">
          <p className="text-[#FCF7EE] text-base mb-4 sm:mb-0">
            © Kapta Studios 2025
          </p>

          {/* Social Icons */}
          <div className="flex space-x-4">
            <a
              href="#"
              className="w-12 h-12 border border-[#6B635D] rounded-full flex items-center justify-center hover:bg-white hover:bg-opacity-10 transition-colors duration-200"
            >
              <InstagramIcon className="w-6 h-6" />
            </a>
            <a
              href="#"
              className="w-12 h-12 border border-[#6B635D] rounded-full flex items-center justify-center hover:bg-white hover:bg-opacity-10 transition-colors duration-200"
            >
              <FacebookIconOutline className="w-6 h-6" color="white" />
            </a>
            <a
              href="#"
              className="w-12 h-12 border border-[#6B635D] rounded-full flex items-center justify-center hover:bg-white hover:bg-opacity-10 transition-colors duration-200"
            >
              <TwitterIcon className="w-6 h-6" />
            </a>
            <a
              href="#"
              className="w-12 h-12 border border-[#6B635D] rounded-full flex items-center justify-center hover:bg-white hover:bg-opacity-10 transition-colors duration-200"
            >
              <LinkedInIcon className="w-6 h-6" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;